import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Table, Modal, Spin, Col, Select, Row } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { get, toLower } from "lodash";
import { WarningOutlined, SaveOutlined, EditOutlined } from "@ant-design/icons";
import ProjectInvoice from "../../../../../services/api/ProjectInvoice";
import { useParams } from "react-router-dom";
import { setLoading } from "../../../../../redux/ducks/Loading";
import { formatNumber, showError, showSuccess, trimData } from "../../../../../utils/utilities";
import { dbDate, langDate, paginationDefaults } from "../../../../../utils/properties";
import { useFormik } from "formik";
import Project from "../../../../../services/api/Project";
import Button from "../../../../../components/functional_components/Button/PFButton";
import dayjs from "dayjs";
const { Option } = Select;

const Invoice = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langDate[toLower(currentLanguage)];
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const projectManagement = useSelector((state) => get(state.ProjectManagementDuck, "project", {}));
  const [state, setState] = useState({
    tableContent: [],
    totalsValue: {},
    modal: {
      visible: false,
      data: {},
    },
    projects: [],
    loading: false,
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
  });

  useEffect(() => {
    getData();
  }, []);

  const getData = async (page = state.pagination.currentPage) => {
    const data = await ProjectInvoice.getDataProjectInvoice(
      params.id,
      `page=${page - 1}&size=${state.pagination.size}&sort=invoiceDate;desc,id;desc`
    );
    if (!data.error) {
      let totalsValue = state.totalsValue;
      if (data.totalsValue) {
        totalsValue = {
          netCompanyAmount: data.totalsValue.totalNetCompanyAmount,
          netPositionAmount: data.totalsValue.totalNetPositionAmount,
          positionAmount: data.totalsValue.totalPositionAmount,
          compensatedAmount: data.totalsValue.totalCompensatedAmount,
        };
        if (data.data.length > 0) {
          totalsValue.currency = data.data[0].currency;
        }
      }
      setState((prevState) => ({
        ...prevState,
        tableContent: data.data,
        totalsValue,
        pagination: {
          currentPage: data.currentPage ? data.currentPage + 1 : paginationDefaults.page,
          size: get(data, "pageSize", paginationDefaults.size),
          totalItems: get(data, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(data, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const columns = [
    {
      title: t("projectsManagement.planning.invoice.referenceInvoiceId"),
      dataIndex: "referenceInvoiceId", // present only for invoice annulments
      key: "referenceInvoiceId",
    },
    {
      title: t("projectsManagement.planning.invoice.id"),
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("projectsManagement.planning.invoice.documentType"),
      dataIndex: "documentType",
      key: "documentType",
      render: (type) => (type ? t(`projectsManagement.planning.invoice.documentTypes.${type}`) : "-"),
    },
    {
      title: t("projectsManagement.planning.invoice.invoiceDate"),
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      render: (date) => date !== null && dayjs(date, dbDate).format(dateFormat),
    },
    {
      title: t("projectsManagement.planning.invoice.dueDate"),
      dataIndex: "dueDate",
      key: "dueDate",
      render: (date) => date !== null && dayjs(date, dbDate).format(dateFormat),
    },
    {
      title: t("projectsManagement.planning.invoice.postDate"),
      dataIndex: "postDate",
      key: "postDate",
      render: (date) => date !== null && dayjs(date, dbDate).format(dateFormat),
    },
    {
      title: t("projectsManagement.planning.invoice.netCompanyAmount"),
      dataIndex: "netCompanyAmount",
      key: "netCompanyAmount",
      className: "table-value-right",
      render: (amount, row) =>
        // an invoice will have a positive amount; a credit note or invoice annulment will have a negative amount
        formatNumber({ value: amount, suffix: row.currency, lang: currentLanguage, decimalScale: 2 }),
    },
    {
      title: t("projectsManagement.planning.invoice.positionAmount"),
      dataIndex: "positionAmount",
      key: "positionAmount",
      className: "table-value-right",
      render: (amount, row) =>
        // an invoice will have a positive amount; a credit note or invoice annulment will have a negative amount
        formatNumber({ value: amount, suffix: row.currency, lang: currentLanguage, decimalScale: 2 }),
    },
    {
      title: t("projectsManagement.planning.invoice.compensatedAmount"),
      dataIndex: "compensatedAmount",
      key: "compensatedAmount",
      className: "table-value-right",
      render: (amount, row) =>
        amount ? formatNumber({ value: amount, suffix: row.currency, lang: currentLanguage, decimalScale: 2 }) : "-",
    },
    {
      key: "action",
      render: (data) =>
        data.id !== "totals" ? (
          <div key={data.id} className="flex-end">
            {data.alert && (
              <div>
                <WarningOutlined className="PFred mr-10 icon-small noCursorPointer" />
              </div>
            )}
            <div
              className="mr-10"
              onClick={
                userInfo.canEditProjectForm && data.actual && projectManagement.enabled ? openModal(data) : undefined
              }
            >
              <EditOutlined
                className={`icon-small ${
                  userInfo.canEditProjectForm && data.actual && projectManagement.enabled
                    ? ""
                    : "disabled noCursorPointer"
                }`}
              />
            </div>
          </div>
        ) : (
          ""
        ),
    },
  ];

  const openModal = (data) => () => {
    getProjects();
    setState((prevState) => ({ ...prevState, modal: { visible: true, data: data } }));
  };

  const closeModal = () => {
    formikInvoice.resetForm();
    getData();
    setState((prevState) => ({
      ...prevState,
      modal: { visible: false, data: {} },
    }));
  };

  const formikInvoice = useFormik({
    initialValues: {
      projects: [],
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.projects) {
        errors.projects = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      let data = {
        invoiceId: get(state.modal, "data.id", ""),
        newProjectId: values.projects,
      };
      const response = await ProjectInvoice.changeProject(data);
      if (!response.error) {
        showSuccess();
      } else {
        showError(get(response, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
      getData();
      closeModal();
    },
  });

  const getProjects = async () => {
    const projects = await Project.listProject("allPages=true");
    if (!projects.error) {
      setState((prevState) => ({
        ...prevState,
        projects: get(projects, "data", []),
      }));
    }
    return projects;
  };

  return (
    <>
      <Table
        bordered
        dataSource={state.tableContent}
        columns={columns}
        rowKey={"id"}
        size="small"
        scroll={{ x: true }}
        pagination={{
          size: "small",
          showSizeChanger: false,
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            getData(page);
          },
        }}
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row className="highlighted">
              <Table.Summary.Cell index={0}>{t("projectsManagement.planning.invoice.totals")}</Table.Summary.Cell>
              <Table.Summary.Cell index={1}></Table.Summary.Cell> {/* invoiceId vuota */}
              <Table.Summary.Cell index={2}></Table.Summary.Cell> {/* documentType vuota */}
              <Table.Summary.Cell index={3}></Table.Summary.Cell> {/* invoiceDate vuota */}
              <Table.Summary.Cell index={4}></Table.Summary.Cell> {/* dueDate vuota */}
              <Table.Summary.Cell index={5}></Table.Summary.Cell> {/* postDate vuota */}
              <Table.Summary.Cell index={6} align="right">
                {formatNumber({
                  value: state.totalsValue.netCompanyAmount,
                  suffix: state.totalsValue.currency,
                  lang: currentLanguage,
                  decimalScale: 2,
                })}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                {formatNumber({
                  value: state.totalsValue.positionAmount,
                  suffix: state.totalsValue.currency,
                  lang: currentLanguage,
                  decimalScale: 2,
                })}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                {formatNumber({
                  value: state.totalsValue.compensatedAmount,
                  suffix: state.totalsValue.currency,
                  lang: currentLanguage,
                  decimalScale: 2,
                })}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7}></Table.Summary.Cell> {/* action vuota */}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />

      {state.modal.visible && (
        <Modal
          title={<b>{t("projectsManagement.planning.invoice.modal.title")}</b>}
          open={state.modal.visible}
          onCancel={closeModal}
          footer={[
            <Button
              key="okButton"
              currentIcon={<SaveOutlined className="icon-medium" />}
              clickCallback={formikInvoice.submitForm}
              isDisabled={!(formikInvoice.dirty && formikInvoice.isValid)}
              isLoading={state.loading}
            />,
          ]}
        >
          {state.loading && (
            <div className="container-spinner-positioning">
              <Spin spinning={state.loading} className="spinner-positioning" />
            </div>
          )}
          <Col lg={8} md={8} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mb-10" htmlFor="projects">
                {t("projectsManagement.planning.invoice.modal.list")}
              </label>
            </Row>
            <Row>
              <Select
                showSearch
                placeholder={t("projectsManagement.planning.invoice.modal.projects")}
                id="projects"
                name="projects"
                className="w-300"
                onBlur={formikInvoice.handleBlur("projects")}
                onChange={formikInvoice.handleChange("projects")}
                value={formikInvoice.values.projects}
              >
                {state.projects.map((item) => (
                  <Option key={item.id} value={item.id} name={item.id}>{`${item.id} | ${item.name}`}</Option>
                ))}
              </Select>
            </Row>
          </Col>
        </Modal>
      )}
    </>
  );
};

export default Invoice;
